import { FC } from "react";
import { observer } from "mobx-react";

import { isFailure, isOk, isPending } from "@/common/common";
import {
  CompanySearchHit,
  CountryOption,
  formatIdentifier,
} from "@/companyRegistry/companyRegistryApiClient";
import { Alert } from "@/components-v2/Alert";
import { Autocomplete } from "@/components-v2/Autocomplete";
import { Autocomplete2 } from "@/components-v2/Autocomplete2";
import { Box, Grid, GridItem, VBox } from "@/components-v2/Box";
import { Button, IconButton, LinkButton } from "@/components-v2/Button";
import { Checkbox, MCheckboxGroup } from "@/components-v2/Checkbox";
import { MDateInput, MTimeInput } from "@/components-v2/DateInput";
import { Dialog } from "@/components-v2/Dialog";
import { MSelect, SelectItem } from "@/components-v2/Select";
import { Tag } from "@/components-v2/Tag";
import { MTextInput } from "@/components-v2/TextInput";
import { useDispatch, useRootStore, useTranslation } from "@/stores/RootStoreContext";
import { handleEnter } from "@/utils/keyboardEvents";

import {
  addParentShareClass,
  AddCompanyState,
  canAddParentShareClass,
  heldError,
  isPercentHeld,
  isShareClassTaken,
  filterParents,
  ParentHolding,
  ParentOption,
  parentOptions,
  removeParentShareClass,
  selectParent,
  selectParentBasis,
  blockedMessage,
  cancelAddCompany,
  canSubmit,
  canVerify,
  changeIdentifier,
  checkIdentifier,
  foundNoCompanies,
  nameSearchHits,
  searchesByName,
  selectSearchHit,
  countryRules,
  identifierError,
  identifierExample,
  identifierRule,
  identifierWarning,
  asksForEntityType,
  isBlocked,
  isRegisterCountry,
  selectCountry,
  selectEntityType,
  setIdentifier,
  parentShareCount,
  showsParent,
  showsParentOwnership,
  showsEntityType,
  showsIdentifier,
  showsRemainingDetails,
  showsSubdivision,
  showsSubscription,
  showsSubdivisionChoice,
  showsVerify,
  subdivisionLabel,
  submitAddCompany,
  subscriptionModelFor,
  verifyIdentifier,
} from "./addCompanyDomain";

export const AddCompanyDialog: FC = observer(() => {
  const state = useRootStore().addCompany;
  return state ? <AddCompanyDialogView state={state} /> : null;
});

const AddCompanyDialogView: FC<{ state: AddCompanyState }> = observer(
  function AddCompanyDialogView({ state }: { state: AddCompanyState }) {
    const dispatch = useDispatch();
    const { t, lang } = useTranslation();
    const rules = countryRules(state);
    const rule = identifierRule(state);
    const blocked = isBlocked(state);
    const message = blockedMessage(state);
    const identifierMessage = identifierError(state);
    const identifierCaution = identifierWarning(state);
    const remaining = showsRemainingDetails(state);
    const verifyOnEnter = handleEnter((event) => {
      if (showsVerify(state)) event.preventDefault();
      if (canVerify(state)) dispatch(verifyIdentifier(state));
    });

    return (
      <Dialog
        title={{ key: "add-company.title", params: { account: state.accountName } }}
        maxWidth={"sm"}
        onCancel={() => dispatch(cancelAddCompany())}
        onSubmit={() => dispatch(submitAddCompany(state))}
        isSubmitDisabled={!canSubmit(state)}
        isSubmitting={isPending(state.submitResult)}
        submitLabel={{ key: "add-company.submit" }}
        submitResult={state.submitResult}
        alertPosition={"bottom"}
      >
        <VBox gap={"lg"}>
          <Box>{t("add-company.intro")}</Box>

          <VBox gap={"md"}>
            <Box fontWeight={"bold"}>{t("add-company.company-details")}</Box>

            <Autocomplete<CountryOption>
              label={{ key: "add-company.country-of-registration" }}
              placeholder={t("add-company.search-select")}
              items={isOk(state.countriesResult) ? state.countriesResult.data : []}
              value={state.country ?? null}
              idFn={(country) => country.iso2}
              labelFn={(country) => country.name[lang]}
              onSelect={(country) => dispatch(selectCountry(country))}
              loading={isPending(state.countriesResult)}
              fullWidth
            />

            {(isFailure(state.countriesResult) || isFailure(state.rulesResult)) && (
              <Alert message={{ key: "error.fetch.data" }} type={"error"} />
            )}

            {rules && showsEntityType(state) && (
              <MSelect
                model={state.entityTypeModel}
                onSelect={(entityType) => dispatch([selectEntityType(entityType)])}
                label={t("add-company.entity-type")}
                placeholder={t("add-company.choose-entity-type")}
                disabled={!asksForEntityType(state)}
                fullWidth
              />
            )}

            {blocked && message && <Alert message={{ translated: message }} type={"error"} />}

            {showsIdentifier(state) && (
              <>
                {showsSubdivision(state) && (
                  <MSelect
                    model={state.subdivisionOptionModel}
                    label={t(subdivisionLabel(state))}
                    placeholder={t("add-company.choose-jurisdiction")}
                    fullWidth
                  />
                )}

                {searchesByName(state) ? (
                  <VBox
                    htmlProps={{
                      onKeyDown: verifyOnEnter,
                      onBlur: () => dispatch([checkIdentifier()]),
                    }}
                  >
                    <Autocomplete2<CompanySearchHit>
                      items={nameSearchHits(state)}
                      itemTextFn={(hit) => hit.name}
                      itemSearchTextFn={(hit) => [hit.name, hit.identifier]}
                      renderItemFn={(hit, _, itemState) => (
                        <SelectItem
                          label={hit.name}
                          caption={rule ? formatIdentifier(hit.identifier, rule) : hit.identifier}
                          active={itemState.highlighted}
                        />
                      )}
                      value={state.identifierModel.inputValue}
                      onValueChange={(value, details) => {
                        if (details.reason === "input-change" || details.reason === "input-clear") {
                          dispatch(changeIdentifier(state, value));
                        }
                      }}
                      onSelect={(hit) => dispatch(selectSearchHit(hit))}
                      emptyMessage={
                        foundNoCompanies(state) ? t("add-company.search-no-companies") : undefined
                      }
                      inputProps={{
                        label: t("add-company.identifier-or-name", {
                          identifier: rule ? rule.label[lang] : "",
                        }),
                        placeholder: identifierExample(state),
                        error: identifierMessage !== undefined,
                        message: identifierMessage && t(identifierMessage),
                      }}
                    />
                  </VBox>
                ) : (
                  <MTextInput
                    model={state.identifierModel}
                    label={rule ? rule.label[lang] : t("add-company.mandatory")}
                    placeholder={identifierExample(state)}
                    onChange={(event) => dispatch([setIdentifier(event.target.value)])}
                    onBlur={() => dispatch([checkIdentifier()])}
                    onKeyDown={verifyOnEnter}
                    error={identifierMessage !== undefined}
                    message={identifierMessage && t(identifierMessage)}
                  />
                )}

                {identifierCaution && <Alert message={identifierCaution} type={"warning"} />}

                {showsVerify(state) && (
                  <Button
                    variant={"secondary"}
                    icon={"check-circle"}
                    label={{ key: "add-company.verify-and-continue" }}
                    loading={isPending(state.lookupResult)}
                    disabled={!state.identifierModel.inputValue}
                    onClick={() => dispatch(verifyIdentifier(state))}
                    fullWidth
                  />
                )}

                {isOk(state.lookupResult) && !state.lookupResult.data.name && (
                  <Alert message={{ key: "add-company.verify-not-found" }} type={"info"} />
                )}

                {isFailure(state.lookupResult) && (
                  <Alert message={{ key: "add-company.verify-failed" }} type={"error"} />
                )}

                {remaining && (
                  <MTextInput
                    model={state.nameModel}
                    label={t("add-company.company-name")}
                    placeholder={t("add-company.mandatory")}
                  />
                )}

                {remaining && showsSubdivisionChoice(state) && (
                  <MSelect
                    model={state.subdivisionOptionModel}
                    label={t(subdivisionLabel(state))}
                    placeholder={t("optional")}
                    fullWidth
                  />
                )}

                {remaining && (
                  <MTextInput
                    model={state.tinNumberModel}
                    label={t("settings.company.tinNumber")}
                    placeholder={t("optional")}
                  />
                )}
              </>
            )}
          </VBox>

          {remaining && <AccountDetails state={state} />}
        </VBox>
      </Dialog>
    );
  },
);

const PercentAddon: FC = () => (
  <Box paddingRight={"sm"} fg={"subtle"}>
    %
  </Box>
);

const ShareStructureNote: FC<{ state: AddCompanyState }> = observer(function ShareStructureNote({
  state,
}: {
  state: AddCompanyState;
}) {
  const shares = parentShareCount(state);
  return shares === undefined ? null : (
    <Alert
      type={"info"}
      message={{
        key: "add-company.share-structure-note",
        params: { shares: { type: "number", value: shares } },
      }}
    />
  );
});

const BasisSelect: FC<{ state: AddCompanyState; holding: ParentHolding; label?: string }> =
  observer(function BasisSelect({
    state,
    holding,
    label,
  }: {
    state: AddCompanyState;
    holding: ParentHolding;
    label?: string;
  }) {
    const dispatch = useDispatch();
    return (
      <MSelect
        model={holding.basisModel}
        label={label}
        onSelect={(basis) => dispatch([selectParentBasis(holding.id, basis)])}
        disabledReasonFn={(basis) =>
          isShareClassTaken(state, basis, holding.id)
            ? { key: "add-company.share-class-taken" }
            : undefined
        }
        fullWidth
      />
    );
  });

const PercentHeld: FC<{ state: AddCompanyState }> = observer(function PercentHeld({
  state,
}: {
  state: AddCompanyState;
}) {
  const { t } = useTranslation();
  const [holding] = state.parentHoldings;
  return (
    <VBox gap={"xs"}>
      <Box>{t("add-company.parent-ownership")}</Box>
      <Grid cols={3} gap={"md"}>
        <GridItem colSpan={1}>
          {holding && <BasisSelect state={state} holding={holding} />}
        </GridItem>
        <GridItem colSpan={2}>
          <MTextInput
            model={state.parentOwnershipModel}
            align={"right"}
            addon={<PercentAddon />}
            inputProps={{ "aria-label": t("add-company.parent-ownership") }}
          />
        </GridItem>
      </Grid>
    </VBox>
  );
});

const SharesHeld: FC<{ state: AddCompanyState }> = observer(function SharesHeld({
  state,
}: {
  state: AddCompanyState;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      {state.parentHoldings.map((holding, index) => {
        const first = index === 0;
        const error = heldError(holding);
        return (
          <Box key={holding.id} gap={"md"} alignItems={"end"}>
            <Box flex={"auto"}>
              <Grid cols={3} gap={"md"}>
                <BasisSelect
                  state={state}
                  holding={holding}
                  label={first ? t("add-company.parent-ownership") : undefined}
                />
                <MTextInput
                  model={holding.heldModel}
                  label={first ? t("add-company.shares-held") : undefined}
                  inputProps={{ "aria-label": t("add-company.shares-held") }}
                  error={error !== undefined || undefined}
                  message={error && t(error)}
                />
                <MTextInput
                  model={holding.totalModel}
                  label={first ? t("add-company.shares-of-total") : undefined}
                  inputProps={{ "aria-label": t("add-company.shares-of-total") }}
                />
              </Grid>
            </Box>
            <IconButton
              icon={"trash"}
              color={"error"}
              tooltip={t("delete")}
              className={first ? "invisible" : undefined}
              disabled={first}
              onClick={[removeParentShareClass(holding.id)]}
            />
          </Box>
        );
      })}
      {canAddParentShareClass(state) && (
        <Box>
          <LinkButton
            icon={"plus"}
            label={{ key: "add-company.add-share-class" }}
            onClick={[addParentShareClass()]}
          />
        </Box>
      )}
    </VBox>
  );
});

const ParentDetails: FC<{ state: AddCompanyState }> = observer(function ParentDetails({
  state,
}: {
  state: AddCompanyState;
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <Box fontWeight={"bold"}>{t("add-company.parent")}</Box>
      <Autocomplete<ParentOption>
        hideCaret
        placeholder={t("search")}
        items={parentOptions(state)}
        value={state.parentModel.value}
        idFn={(parent) => parent.orgNo}
        labelFn={(parent) => parent.name}
        captionFn={(parent) => ({ type: "org-no", value: parent.orgNo })}
        filterFn={({ items }, query) => filterParents(items, query)}
        onSelect={(parent) => dispatch([selectParent(parent)])}
        onClear={() => dispatch([selectParent(null)])}
        fullWidth
      />
      {showsParentOwnership(state) && (
        <>
          {isPercentHeld(state) ? <PercentHeld state={state} /> : <SharesHeld state={state} />}
          <ShareStructureNote state={state} />
          <VBox gap={"xs"}>
            <Box>{t("add-company.parent-ownership-registration-date")}</Box>
            <Grid cols={2} gap={"md"}>
              <MDateInput model={state.parentRegistrationDateModel} />
              <MTimeInput model={state.parentRegistrationTimeModel} />
            </Grid>
          </VBox>
        </>
      )}
    </VBox>
  );
});

const AccountDetails: FC<{ state: AddCompanyState }> = observer(function AccountDetails({
  state,
}: {
  state: AddCompanyState;
}) {
  const { t } = useTranslation();
  return (
    <>
      <VBox gap={"md"}>
        <Box fontWeight={"bold"}>{t("add-company.account-details")}</Box>
        {showsSubscription(state) && (
          <MSelect
            model={subscriptionModelFor(state)}
            label={t("add-company.activate-subscription")}
            disabled={isRegisterCountry(state)}
            fullWidth
          />
        )}
        {showsParent(state) && <ParentDetails state={state} />}
        <MSelect
          model={state.portfolioModel}
          label={t("add-company.add-to-portfolio")}
          placeholder={t("optional")}
          fullWidth
        />
      </VBox>

      {state.adminsModel && (
        <VBox gap={"md"}>
          <Box fontWeight={"bold"}>{t("add-company.administrators")}</Box>
          <Box>
            <LinkButton
              label={{ key: "add-company.select-all-none" }}
              onClick={() => {
                const model = state.adminsModel;
                if (!model) return;
                if (model.selectedIds.size === model.options.length) model.clear();
                else model.selectAll();
              }}
            />
          </Box>
          <MCheckboxGroup
            model={state.adminsModel}
            labelFn={(admin) => (
              <Box alignItems={"center"} gap={"sm"}>
                {admin.name}
                {admin.isAccountAdmin && (
                  <Tag label={{ key: "enterprise-account.account-admin" }} color={"neutral"} />
                )}
              </Box>
            )}
          />
        </VBox>
      )}

      <VBox gap={"md"}>
        <Box fontWeight={"bold"}>{t("add-company.terms-and-conditions")}</Box>
        <Checkbox
          label={{ key: "add-company.accept-terms" }}
          checked={state.acceptTerms.value}
          onChange={(e) => state.acceptTerms.setValue(e.target.checked)}
        />
      </VBox>
    </>
  );
});
